import {
  HttpInterceptorFn,
  HttpRequest,
  HttpEvent,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, Observable, EMPTY } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  backendErrorMessage,
  isAuthFailure,
  validationErrors,
} from '../../http/backend-error.util';

// Define ACTUAL BACKEND API URLs that do NOT require a token.
// This should NOT include frontend Angular routes like '/ES/login'.
// Example: If your login API endpoint is 'https://your-api.com/api/v1/auth/login',
// you might add '/api/v1/auth/login' or a unique part of it.
const PUBLIC_API_ENDPOINTS: string[] = [
  // Add your actual public BACKEND API endpoint segments here.
  // For example, if your login page calls '/api/authenticate'
  // '/api/authenticate',
  // '/api/users/register',
  // '/api/public-data'
];

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next
): Observable<HttpEvent<any>> => {
  const _matSnackBar = inject(MatSnackBar);
  const router = inject(Router);

  // 1. Check if the current Angular route IS one of the public pages (login/signup).
  const currentAngularRoute = router.url;
  const onSpanishLoginPage =
    currentAngularRoute.startsWith('/ES/inicio-sesion');
  const onEnglishLoginPage = currentAngularRoute.startsWith('/EN/login');
  const onSpanishSignupPage = currentAngularRoute.startsWith('/ES/registro');
  const onEnglishSignupPage = currentAngularRoute.startsWith('/EN/sign-up');
  const onAnyPublicPage =
    onSpanishLoginPage ||
    onEnglishLoginPage ||
    onSpanishSignupPage ||
    onEnglishSignupPage;

  if (onAnyPublicPage) {
    // If we are on any of the specified public pages, allow all HTTP requests to pass through.
    // The component itself is responsible for its API calls.
    // The catchError here is for API calls made BY the public page (e.g., bad credentials, user exists).
    console.log(
      'On a public page (Angular route), request to:',
      req.url,
      'is passing through without token/redirect logic.'
    );
    return next(req).pipe(
      catchError((err) => {
        // Allow specific API calls (like passive email validation) to handle their own errors (e.g., 400 or 401)
        if (req.url.includes('/email-dos') && (isAuthFailure(err) || err.status === 400)) {
          return throwError(() => err);
        }

        if (isAuthFailure(err)) {
          _matSnackBar.open(
            'Error de autenticación. Verifica tus credenciales.',
            'X',
            {
              duration: 7000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
            }
          );
        } else if (err.status === 404) {
          // 404 errors are expected (e.g., user doesn't exist during signup check)
          // Let the component handle this
          return throwError(() => err);
        } else {
          logValidationErrors(req, err);
          _matSnackBar.open(backendErrorMessage(err) ?? 'Ocurrió un error durante el proceso.', 'X', {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          });
        }
        return throwError(() => err); // Propagate the error for the component to handle
      })
    );
  }

  // 2. If NOT on a login page (Angular route), then apply token logic.
  const token = localStorage.getItem('TokenServe');

  if (!token) {
    // No token, and we're NOT on a login page.
    console.warn(
      'No token found for a protected route. Redirecting to login. Request was to:',
      req.url
    );
    _matSnackBar.open('Acceso denegado. Por favor, inicia sesión.', 'X', {
      duration: 7000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
    // Determine which login page to redirect to
    const currentLang = localStorage.getItem('language') || 'es';
    const loginRedirectPath =
      currentLang === 'es' ? '/ES/inicio-sesion' : '/EN/login';
    router.navigate([loginRedirectPath]);
    return EMPTY; // Stop the request
  }

  // Token exists, add it to the request.
  const authReq = req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });

  return next(authReq).pipe(
    catchError((err) => {
      // Ignorar redirección y errores globales para validación pasiva de correos
      if (req.url.includes('/email-dos') && (isAuthFailure(err) || err.status === 400)) {
        return throwError(() => err);
      }

      if (isAuthFailure(err)) {
        // Token was sent but was invalid/expired
        _matSnackBar.open(
          'Sesión no válida o expirada. Por favor, inicia sesión nuevamente.',
          'X',
          {
            duration: 10000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
          }
        );
        localStorage.removeItem('TokenServe');
        const currentLang = localStorage.getItem('language') || 'es';
        const loginRedirectPath =
          currentLang === 'es' ? '/ES/inicio-sesion' : '/EN/login';
        router.navigate([loginRedirectPath]);
      } else {
        logValidationErrors(req, err);
        _matSnackBar.open(backendErrorMessage(err) ?? 'Ocurrió un error en la solicitud.', 'X', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      }
      return throwError(() => err);
    })
  );
};

/** Deja en consola qué campos rechazó la validación del backend (ProblemDetails). */
function logValidationErrors(req: HttpRequest<any>, err: unknown): void {
  const errors = validationErrors(err);
  if (errors) {
    console.warn('Validation errors from', req.method, req.url, errors);
  }
}
